/**
 * LanguageSwitch — LE sélecteur de langue FR|NL|EN, partagé.
 *
 * Il vivait en JSX inline dans `PublicHeader`, donc il n'existait que sur
 * les pages publiques : une fois connecté, l'utilisateur n'avait plus
 * aucun moyen de changer de langue sans aller dans /settings. Le monter
 * aussi dans l'app demandait soit de le recopier — une deuxième pastille
 * qui aurait dérivé de la première, comme le tiroir mobile a déjà dérivé
 * avec ses classes `sv2-menu-lang-*` — soit de l'extraire. C'est ce
 * fichier.
 *
 * Deux exports, parce que deux besoins distincts :
 *
 *  · `useLanguageChoice()` — la LOGIQUE : écrire le choix. Les Paramètres
 *    s'en servent pour leur `<select>`, qui garde son apparence de champ
 *    de formulaire mais doit persister exactement comme la pastille.
 *  · `LanguageSwitch` — le RENDU : la pastille elle-même.
 *
 * Écrire une langue, c'est deux gestes indissociables, et c'est pour les
 * tenir ensemble que le hook existe :
 *   1. `setLanguage` → `archer_locale` + LOCALE_CHANGE_EVENT, donc toute
 *      l'interface (site public, barre latérale, bureau, page dossier)
 *      bascule dans la seconde, sans rechargement ;
 *   2. `PUT /api/profile` si quelqu'un est connecté → `users.language`,
 *      la mémoire qui traverse les appareils et qui dit au pipeline dans
 *      quelle langue rédiger (`backend/utils/user_language.py`).
 *
 * L'appel réseau est délibérément silencieux (`.catch(() => {})`) : la
 * langue a déjà changé à l'écran, et une erreur de persistance ne doit
 * pas remonter une alerte au visiteur qui vient simplement de cliquer
 * « NL ». Le store local reste juste ; seul le report au compte est perdu.
 */
import React, { useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useSiteLanguage } from '../hooks/useSiteLanguage';
import { normalizeLang } from '../hooks/useAppLanguage';
import '../styles/language-switch.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Les trois langues exposées. Le store sait aussi lire 'de' et 'es', mais
 * aucun dictionnaire ne les porte : les proposer ne ferait que basculer
 * l'interface en français par repli.
 */
export const LANG_OPTIONS = ['FR', 'NL', 'EN'];

/**
 * Langue courante + le seul chemin d'écriture du produit.
 *
 * @returns {[string, (next: string) => string]} `[language, chooseLanguage]`
 */
export function useLanguageChoice() {
  const { user } = useAuth();
  const [language, setLanguage] = useSiteLanguage();

  const chooseLanguage = useCallback((next) => {
    const lang = normalizeLang(next);
    setLanguage(lang);
    if (user) {
      axios.put(`${API}/profile`, { language: lang }, { withCredentials: true }).catch(() => {});
    }
    return lang;
  }, [user, setLanguage]);

  return [language, chooseLanguage];
}

/**
 * La pastille. Sans prop, elle lit et écrit la langue elle-même — c'est le
 * cas de l'app connectée. `PublicHeader` lui passe au contraire sa propre
 * paire : il tient déjà la langue pour ses libellés de nav et alimente le
 * tiroir mobile avec le même gestionnaire, il ne doit pas y avoir deux
 * abonnements au store dans une seule barre.
 *
 * @param {string}   [language]     langue affichée comme active
 * @param {Function} [onChange]     appelé avec 'fr' | 'nl' | 'en'
 * @param {string}   [testIdPrefix] préfixe des `data-testid` des boutons
 * @param {string}   [className]    classe supplémentaire sur le conteneur
 */
export default function LanguageSwitch({
  language,
  onChange,
  testIdPrefix = 'lang',
  className = '',
}) {
  const [ownLanguage, ownChoose] = useLanguageChoice();
  const current = language ?? ownLanguage;
  const choose = onChange ?? ownChoose;

  return (
    <div className={`lang-switch${className ? ` ${className}` : ''}`} role="tablist" aria-label="Language">
      {LANG_OPTIONS.map((l) => {
        const active = String(current || '').toUpperCase() === l;
        return (
          <button
            key={l}
            type="button"
            role="tab"
            aria-selected={active}
            className={`lang-opt${active ? ' active' : ''}`}
            onClick={() => choose(l.toLowerCase())}
            data-testid={`${testIdPrefix}-${l.toLowerCase()}`}
          >
            {l}
          </button>
        );
      })}
    </div>
  );
}
